<template>
  <div>
    <b-modal
      v-model="isVisible"
      :title="$t('general.files', { count: items.length })"
      hide-footer
      centered
      size="md"
    >
      <div class="preupload-modal">
        <div v-if="!items.length" class="empty">
          <feather-icon icon="FileIcon" size="36" />
          <div class="mt-1">{{ $t('general.noFilesFound') }}</div>
        </div>

        <div v-else class="file-list">
          <div v-for="(it, idx) in items" :key="idx" class="file-row">
            <div class="thumb">
              <img v-if="isImage(it.file.name)" :src="it.url" alt="preview" />
              <video v-else-if="isVideo(it.file.name)" :src="it.url" />
              <feather-icon v-else icon="FileIcon" size="20" />
            </div>
            <div class="meta">
              <div class="name" :title="it.file.name">{{ it.file.name }}</div>
              <div class="size">{{ formatSize(it.file.size) }}</div>
            </div>
            <feather-icon
              class="remove"
              icon="XIcon"
              size="16"
              @click="remove(idx)"
            />
          </div>
        </div>

        <div class="actions">
          <b-button variant="outline-secondary" @click="cancel">{{
            $t('button.cancel')
          }}</b-button>
          <b-button
            variant="primary"
            :disabled="!items.length"
            @click="confirm"
            >{{ $t('chat.send') }}</b-button
          >
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import { BModal, BButton } from 'bootstrap-vue';
  import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue';
  import { isImage, isVideo } from '@/helpers/fileHelper';

  export default {
    name: 'MultiFilePreUploadModal',
    components: { BModal, BButton, FeatherIcon },
    props: {
      value: { type: Boolean, default: false },
      files: { type: Array, default: () => [] }
    },
    data() {
      return {
        items: []
      };
    },
    watch: {
      value: {
        immediate: true,
        handler(v) {
          if (v) {
            this.items = (this.files || []).map(f => ({
              file: f,
              url: URL.createObjectURL(f)
            }));
          } else {
            this.revokeAll();
            this.items = [];
          }
        }
      }
    },
    computed: {
      isVisible: {
        get() {
          return this.value;
        },
        set(v) {
          this.$emit('input', v);
        }
      }
    },
    methods: {
      isImage,
      isVideo,
      remove(idx) {
        const it = this.items[idx];
        if (it?.url) URL.revokeObjectURL(it.url);
        this.items.splice(idx, 1);
        // Auto-close when all files are removed
        if (this.items.length === 0) {
          this.cancel();
        }
      },
      cancel() {
        this.isVisible = false;
        this.$emit('cancel');
      },
      confirm() {
        const files = this.items.map(i => i.file);
        this.isVisible = false;
        this.$emit('confirm', files);
      },
      revokeAll() {
        this.items.forEach(it => {
          if (it?.url) URL.revokeObjectURL(it.url);
        });
      },
      formatSize(bytes) {
        if (!bytes && bytes !== 0) return '';
        const kb = bytes / 1024;
        if (kb < 1024) return `${kb.toFixed(1)} KB`;
        return `${(kb / 1024).toFixed(1)} MB`;
      }
    },
    beforeDestroy() {
      this.revokeAll();
    }
  };
</script>

<style lang="scss" scoped>
  .preupload-modal {
    .file-list {
      max-height: 60vh;
      overflow-y: auto;
      padding-right: 4px;
    }
    .file-row {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 8px 6px;
      border-radius: 8px;
      background: #f9fafb;
      margin-bottom: 8px;
    }
    .thumb {
      width: 50px;
      height: 50px;
      border-radius: 8px;
      overflow: hidden;
      background: #e9ecef;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .thumb img,
    .thumb video {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .meta {
      flex: 1;
      min-width: 0;
    }
    .name {
      font-weight: 500;
      font-size: 16px !important;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-align: start;
    }
    .size {
      font-size: 12px;
      color: #6c757d;
    }
    .remove {
      cursor: pointer;
      color: #dc3545;
    }
    .actions {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 12px;
    }
    .empty {
      text-align: center;
      color: #6c757d;
      padding: 2rem 0;
    }
  }
</style>
